import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Modal, NotificationVariants } from '@common/components';
import { COMMON_TRANSLATION } from '@common/constants';
import { Sizes } from '@common/types';
import { formatDate, formatTime } from '@common/utils';
import { BLANK_VALUE, GENETIC_COUNSELING_TRANSLATION } from '@modules/genetic-counseling/constants';
import { useAddNotification } from '@modules/notifications/store';

import PatientProfileOverviewCard from '../PatientProfileOverviewCard/PatientProfileOverviewCard';
import { MainInfo } from '../PatientProfileOverviewCard/PatientProfileOverviewCard.types';
import Reopen from '../Reopen/Reopen';
import useMutationReopenAppointment from '@/modules/genetic-counseling/hooks/useMutationReopenAppointment';
import { HELIX_SUPPORT_EMAIL, PATHS } from '@/common/constants';
import { ReopenAppointmentResponseValues } from '@/modules/genetic-counseling/types/reopen-appointment';
import { getTimeZoneString } from '@/modules/genetic-counseling/utils';
import { PatientNoShowCardProps } from './PatientNoShowCard.types';

const PatientNoShowCard: FC<PatientNoShowCardProps> = ({ appointmentInfo }) => {
  const { t } = useTranslation([GENETIC_COUNSELING_TRANSLATION, COMMON_TRANSLATION]);
  const addNotification = useAddNotification();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const onSuccess = (data: ReopenAppointmentResponseValues): void => {
    addNotification({
      autoClose: true,
      title: t('reopen.success'),
      description: t('reopen.success_description'),
      type: NotificationVariants.SUCCESS,
    });

    handleCloseModal();
    navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.PATIENT}/${data.appointmentID}`);
  };

  const onError = (): void => {
    addNotification({
      autoClose: true,
      title: t('reopen.error_title'),
      description: t('reopen.error_description', { email: HELIX_SUPPORT_EMAIL }),
      type: NotificationVariants.ERROR,
    });
    handleCloseModal();
  };

  const { mutateAsync, isLoading } = useMutationReopenAppointment(onSuccess, onError);

  const { completionDate, timezone } = appointmentInfo;

  const getCompletionDate = (completionDate: string, timezone: string): string => {
    if (!completionDate || !timezone) {
      return BLANK_VALUE;
    }

    const date = formatDate(completionDate);
    const time = formatTime(completionDate, timezone);
    const tz = getTimeZoneString(completionDate, timezone);

    return t('date_time_human_readable', {
      date,
      ns: COMMON_TRANSLATION,
      time,
      tz,
    });
  };

  const appointmentInfoData: MainInfo[] = [
    {
      id: 'appt-completion-date',
      label: t('no_show.counseling_status_card.completion_date'),
      value: getCompletionDate(completionDate, timezone),
    },
  ];

  const handleReopenReferral = (): void => {
    setIsModalOpen(true);
  };

  const actions = [
    {
      id: 'reopen',
      label: t('reopen_patient_referral.title'),
      onClick: handleReopenReferral,
    },
  ];

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  const handleConfirm = (): void => {
    const id = appointmentInfo.id;

    void mutateAsync({ id });
  };

  return (
    <>
      <PatientProfileOverviewCard
        title={t('no_show.counseling_status_card.title')}
        actions={actions}
        mainInfo={appointmentInfoData}
      />
      <Modal onClose={handleCloseModal} size={Sizes.LARGE} show={isModalOpen} title={t('reopen.title')}>
        <Reopen handleCancel={handleCloseModal} handleConfirm={handleConfirm} isLoading={isLoading} />
      </Modal>
    </>
  );
};

export default PatientNoShowCard;
